import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Col,
  NMPopup,
  Row,
  Button,
  NMForm,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { Soc2Type2Report } from "./Soc2Type2";
import { useDispatch, useSelector } from "react-redux";
import { auditorActions, getCurrentAuditReport } from "../../../store";

const FormFields = [
  {
    attribute: "report_type",
    label: "Report Type",
    required: true,
    typeRepresentation: "DROPDOWN",
    placeholder: "Select",
    classes: { root: "w-20 mb-10", label: "w-120-px" },
    options: [
      // { id: "default", label: "Default" },
      // { id: "iso27001_2022", label: "ISO 27001:2022" },
      { id: "soc2_type2", label: "SOC 2 Type 2" },
    ],
  },
  {
    attribute: "internal_notes",
    label: "Internal Notes:",
    required: true,
    typeRepresentation: "TEXTAREA",
    rows: 3,
    placeholder: "Provide internal notes",
    classes: { root: "w-100 mb-0", label: "w-120-px" },
  },
];

export const AuditReportBluprintCreateScreen = () => {
  const { blueprintId } = useParams();
  const currentReport = useSelector(getCurrentAuditReport);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const _formRef = useRef(null);
  const soc2Type2ReportRef = useRef(null);
  const [state, setState] = useState({
    report_type: "",
    mode: blueprintId ? "view" : "edit",
    show: !Boolean(blueprintId),
    submitting: false,
  });

  const isDraft = currentReport?.status === "draft";
  const isArchived = currentReport?.status === "archived";
  const isEditMode = state.mode === "edit";

  useEffect(() => {
    if (blueprintId) {
      fetchAuditReportBlueprint();
    }
  }, [blueprintId]);

  const fetchAuditReportBlueprint = () => {
    const action = auditorActions.fetchAuditReportBlueprint({
      blueprintId: blueprintId,
    });
    action.onFailure = () => {
      navigate("page-not-found");
    };
    action.onComplete = (bp) => {
      if (bp) {
        const { report_type } = bp;
        setState((state) => ({
          ...state,
          report_type,
          mode: "view",
          show: true,
        }));
        generateTableOfContents(report_type);
      }
    };
    dispatch(action);
  };

  const generateTableOfContents = (report_type) => {
    const timmer = setTimeout(() => {
      clearTimeout(timmer);
      if (report_type === "soc2_type2") {
        soc2Type2ReportRef.current.generateTableOfContents();
      }
    }, 500);
  };

  const updateState = (iNewState = {}) => setState({ ...state, ...iNewState });

  const handleChange = (attribute, value) => {
    if (attribute === "report_type") {
      updateState({ report_type: value });
    }
  };

  const handleEdit = () => {
    setState((state) => ({ ...state, mode: "edit" }));
    if (state.report_type === "soc2_type2") {
      soc2Type2ReportRef.current.setMode("edit");
    }
  };

  const handleSave = useCallback(() => {
    const valid = _formRef.current.validate(true);
    if (!valid) {
      return;
    }
    const formValue = _formRef.current.getValues();
    let payload = { ...formValue };
    if (formValue.report_type === "soc2_type2") {
      payload.content = soc2Type2ReportRef.current.getData();
    }
    updateState({ submitting: true });
    let action;
    if (blueprintId) {
      action = auditorActions.updateAuditReportBlueprint({
        id: blueprintId,
        audit_report_blueprint: payload,
      });
    } else {
      payload.status = "draft";
      payload.config = JSON.stringify({});
      action = auditorActions.createAuditReportBlueprint({
        audit_report_blueprint: payload,
      });
    }
    action.onComplete = () => {
      updateState({ submitting: false, show: false });
      if (blueprintId) {
        fetchAuditReportBlueprint();
      } else {
        navigate(-1);
      }
    };
    dispatch(action);
  }, [blueprintId]);

  const handleActivate = () => {
    setState((state) => ({
      ...state,
      showDialog: true,
      selectedStatus: "active",
    }));
  };

  const handleArchive = () => {
    setState((state) => ({
      ...state,
      showDialog: true,
      selectedStatus: "archive",
    }));
  };

  const handleCancel = () => {
    setState((state) => ({ ...state, mode: "view", show: false }));
    if (state.report_type === "soc2_type2") {
      soc2Type2ReportRef.current.setMode("view");
    }
    setTimeout(() => {
      setState((state) => ({ ...state, show: true }));
      generateTableOfContents(state.report_type);
    }, 10);
  };

  const handleDialogConfirm = () => {
    const { selectedStatus } = state;
    updateState({ submitting: true });
    const action = auditorActions.updateAuditReportBlueprint({
      id: blueprintId,
      audit_report_blueprint: {
        ...currentReport,
        status: selectedStatus,
      },
    });
    action.onComplete = () => {
      updateState({ submitting: false, showDialog: false });
      navigate(-1);
    };
    action.onFailure = () => {
      updateState({ submitting: false, showDialog: false });
    };
    dispatch(action);
  };

  const handleDialogCancel = () => {
    setState((state) => ({ ...state, showDialog: false }));
  };

  const initialData = useMemo(() => {
    if (blueprintId) {
      return currentReport;
    }
    return null;
  }, [blueprintId, currentReport]);

  return (
    <Col className="w-100 h-100 overflow-hide pa-20">
      <Row align="center" justify="between" className="w-100">
        <Link to="/A/R">
          <span className="text-action fs-14 fw-400">Tracker/</span>
        </Link>
        <Row>
          {(!blueprintId || (initialData && isDraft)) && (
            <Button
              label={isEditMode ? "Save" : "Edit"}
              disabled={!state.report_type}
              icon={isEditMode ? "SUBMIT" : "EDIT"}
              classes={{ root: "w-20" }}
              onClick={isEditMode ? handleSave : handleEdit}
            />
          )}
          {isEditMode && initialData && (
            <Button
              label="Cancel"
              className="ml-16"
              variant="CAUTION"
              icon="CANCEL"
              onClick={handleCancel}
            />
          )}
          {initialData && !isArchived && (
            <Button
              label={isDraft ? "Activate" : "Archive"}
              icon={isEditMode ? "SUBMIT" : "EDIT"}
              disabled={isEditMode}
              classes={{ root: "w-20" }}
              className="ml-16"
              onClick={isDraft ? handleActivate : handleArchive}
            />
          )}
        </Row>
      </Row>
      {state.show && (!blueprintId || (blueprintId && currentReport)) ? (
        <>
          <Row className="w-100 mt-10" align="center" justify="between">
            <NMForm
              ref={_formRef}
              fields={FormFields.map((field) => ({
                ...field,
                readOnly:
                  field.attribute === "report_type" && Boolean(blueprintId),
              }))}
              formData={{
                report_type: initialData ? initialData.report_type : "",
                internal_notes: initialData ? initialData.internal_notes : "",
              }}
              onChange={handleChange}
              readOnly={!isEditMode}
            />
          </Row>
          <Col className="w-100 flex-auto overflow-hide">
            {state.report_type === "soc2_type2" && (
              <Soc2Type2Report
                ref={soc2Type2ReportRef}
                initialMode={state.mode}
                initialData={initialData}
              />
            )}
          </Col>
        </>
      ) : (
        <NMWaitIndicator />
      )}
      {state.showDialog && (
        <NMPopup
          variant={state.selectedStatus === "active" ? "INFO" : "CAUTION"}
          visible={true}
          title={
            state.selectedStatus === "active"
              ? "Activate Blueprint?"
              : "Archive Blueprint?"
          }
          actions={[
            {
              label: "Cancel",
              icon: "CANCEL",
              variant: "CAUTION",
              disabled: state.submitting,
              onClick: handleDialogCancel,
            },
            {
              label: "Confirm",
              icon: "SUBMIT",
              loading: state.submitting,
              onClick: handleDialogConfirm,
            },
          ]}
        >
          <Col>
            Are you sure want to{" "}
            {state.selectedStatus === "active" ? "activate" : "archive"} this
            report blueprint?
          </Col>
        </NMPopup>
      )}
    </Col>
  );
};
